import {
  Component,
  Input,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ElementRef,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-information-popup',
    templateUrl: './information-popup.component.html',
    styleUrls: ['./information-popup.component.scss'],
    standalone: false
})
export class InformationPopupComponent implements OnDestroy {
  @Input() information = '';
  @Input() popupAlign: 'start' | 'center' | 'end' = 'center';

  @ViewChild('triggerEl', { static: true }) triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('popupPanel') popupPanel!: TemplateRef<unknown>;

  private overlayRef?: OverlayRef;
  private backdropSub?: Subscription;

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) {}

  togglePopup(event: MouseEvent): void {
    event.stopPropagation();
    if (this.overlayRef?.hasAttached()) {
      this.detachOverlay();
    } else {
      this.attachOverlay();
    }
  }

  closePopup(): void {
    this.detachOverlay();
  }

  private attachOverlay(): void {
    const trigger = this.triggerRef?.nativeElement;
    if (!trigger || !this.popupPanel) {
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(trigger)
      .withPositions(this.getPositions())
      .withPush(false)
      .withViewportMargin(8)
      .withFlexibleDimensions(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'information-popup-overlay-panel',
      minWidth: '280px',
      maxWidth: 'min(90vw, 420px)',
    });

    const portal = new TemplatePortal(this.popupPanel, this.viewContainerRef);
    this.overlayRef.attach(portal);

    this.backdropSub = this.overlayRef.backdropClick().subscribe(() => this.detachOverlay());
  }

  private detachOverlay(): void {
    this.backdropSub?.unsubscribe();
    this.backdropSub = undefined;
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }

  private getPositions() {
    const belowCenter = {
      originX: 'center' as const,
      originY: 'bottom' as const,
      overlayX: 'center' as const,
      overlayY: 'top' as const,
      offsetY: 6,
    };
    const belowStart = {
      originX: 'start' as const,
      originY: 'bottom' as const,
      overlayX: 'start' as const,
      overlayY: 'top' as const,
      offsetY: 6,
    };
    const belowEnd = {
      originX: 'end' as const,
      originY: 'bottom' as const,
      overlayX: 'end' as const,
      overlayY: 'top' as const,
      offsetY: 6,
    };
    const aboveEnd = {
      originX: 'end' as const,
      originY: 'top' as const,
      overlayX: 'end' as const,
      overlayY: 'bottom' as const,
      offsetY: -6,
    };
    const aboveStart = {
      originX: 'start' as const,
      originY: 'top' as const,
      overlayX: 'start' as const,
      overlayY: 'bottom' as const,
      offsetY: -6,
    };
    const aboveCenter = {
      originX: 'center' as const,
      originY: 'top' as const,
      overlayX: 'center' as const,
      overlayY: 'bottom' as const,
      offsetY: -6,
    };

    switch (this.popupAlign) {
      case 'end':
        return [belowEnd, aboveEnd, belowStart, belowCenter, aboveStart, aboveCenter];
      case 'start':
        return [belowStart, aboveStart, belowEnd, belowCenter, aboveEnd, aboveCenter];
      default:
        return [belowCenter, aboveCenter, belowStart, belowEnd, aboveStart, aboveEnd];
    }
  }

  ngOnDestroy(): void {
    this.detachOverlay();
  }
}
